import { deltaE, hexToLab, normalizeColor } from './colorDistance';

// Conflict threshold in CIE76 ΔE (Lab). Industry UI-palette tools put
// "clearly distinguishable" at ΔE 15–25; 20 is the conservative middle.
export const CONFLICT_THRESHOLD = 20;

// Minimum ΔE between any two curated palette entries. Stricter than
// CONFLICT_THRESHOLD because the user-facing pain point is two routes
// looking similar on the same map, not a route looking like a status color.
export const PALETTE_SEPARATION = 28;

// Reserved status colors that route colors must stay clear of.
// Mirrors DispatchPlan/constants.js SELECTED_COLOR, UNASSIGNED_COLOR,
// SCHEDULED_UNASSIGNED_COLOR and the status colors in styles/colors.js.
export const RESERVED_COLORS = [
    '#59b863', // SELECTED_COLOR
    '#2b2b2b', // UNASSIGNED_COLOR
    '#e28700', // SCHEDULED_UNASSIGNED_COLOR / golds.primary
    '#007bff', // blues.inTransit / blues.primary
    '#ff903d', // oranges.rescheduled
    '#dc3545', // reds.primary
    '#d23e3e', // reds.cancelled
];

// Curated palette of 16 colors. Verified by unit tests to be mutually
// distinguishable (ΔE ≥ PALETTE_SEPARATION) and clear of reserved colors
// (ΔE ≥ CONFLICT_THRESHOLD). Locked order — consumers may rely on indices
// for stable color-per-route.
export const CURATED_PALETTE = [
    '#1e3a8a', // deep navy blue
    '#0284c7', // sky blue
    '#06b6d4', // bright cyan
    '#0f766e', // dark teal
    '#15803d', // forest green
    '#65a30d', // lime olive
    '#c05200', // dark rust/orange
    '#78350f', // chocolate brown
    '#9f1239', // deep rose
    '#ec4899', // hot pink
    '#a21caf', // plum magenta
    '#6d28d9', // violet
    '#1540c0', // cobalt blue
    '#2d4a10', // dark olive
    '#475569', // slate gray
    '#997000', // golden mustard
];

const RESERVED_LABS = RESERVED_COLORS.map(hexToLab);

function hasConflict(candidateHex, alreadyEmittedLabs) {
    const lab = hexToLab(candidateHex);
    for (const r of RESERVED_LABS) {
        if (deltaE(lab, r) < CONFLICT_THRESHOLD) return true;
    }
    for (const e of alreadyEmittedLabs) {
        if (deltaE(lab, e) < CONFLICT_THRESHOLD) return true;
    }
    return false;
}

function shiftLightness(hex, deltaPct) {
    const r = parseInt(hex.slice(1, 3), 16) / 255;
    const g = parseInt(hex.slice(3, 5), 16) / 255;
    const b = parseInt(hex.slice(5, 7), 16) / 255;

    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    const l = (max + min) / 2;
    let h = 0;
    let s = 0;
    if (max !== min) {
        const d = max - min;
        s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
        switch (max) {
            case r:
                h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
                break;
            case g:
                h = ((b - r) / d + 2) / 6;
                break;
            default:
                h = ((r - g) / d + 4) / 6;
                break;
        }
    }
    const newL = Math.max(0, Math.min(1, l + deltaPct / 100));

    const hue2rgb = (p, q, t) => {
        if (t < 0) t += 1;
        if (t > 1) t -= 1;
        if (t < 1 / 6) return p + (q - p) * 6 * t;
        if (t < 1 / 2) return q;
        if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
        return p;
    };

    let nr;
    let ng;
    let nb;
    if (s === 0) {
        nr = ng = nb = newL;
    } else {
        const q = newL < 0.5 ? newL * (1 + s) : newL + s - newL * s;
        const p = 2 * newL - q;
        nr = hue2rgb(p, q, h + 1 / 3);
        ng = hue2rgb(p, q, h);
        nb = hue2rgb(p, q, h - 1 / 3);
    }
    const toHex = (n) =>
        Math.round(n * 255)
            .toString(16)
            .padStart(2, '0');
    return `#${toHex(nr)}${toHex(ng)}${toHex(nb)}`;
}

// Deterministic build of 50 route colors.
export function buildRouteColors() {
    const out = [...CURATED_PALETTE];
    const emittedLabs = out.map(hexToLab);

    // Multiple passes with increasing lightness shift magnitude. Smaller shifts
    // first to keep variants visually close to their base hue; larger shifts
    // added until the target of 50 is reached. Each candidate is only accepted
    // if it clears every reserved color and every already-emitted color by
    // at least CONFLICT_THRESHOLD (ΔE). The algorithm is deterministic.
    const OFFSETS = [-12, 12, -22, 22, -6, 6, -30, 30, -35, 35, -40, 40, -18, 18, -45, 45, 25, -25];
    for (const offset of OFFSETS) {
        for (const base of CURATED_PALETTE) {
            if (out.length >= 50) return out;
            const candidate = shiftLightness(base, offset);
            if (candidate === base) continue;
            if (hasConflict(candidate, emittedLabs)) continue;
            out.push(candidate);
            emittedLabs.push(hexToLab(candidate));
        }
    }
    if (out.length < 50) {
        throw new Error(
            `buildRouteColors: produced only ${out.length}/50 unique non-conflicting colors. ` +
                `Extend OFFSETS or adjust CURATED_PALETTE.`
        );
    }
    return out;
}

export function findConflictingReserved(input) {
    const hex = normalizeColor(input);
    if (!hex) return null;
    const lab = hexToLab(hex);
    let closest = null;
    let closestDist = Infinity;
    for (const r of RESERVED_COLORS) {
        const d = deltaE(lab, hexToLab(r));
        if (d < CONFLICT_THRESHOLD && d < closestDist) {
            closest = r;
            closestDist = d;
        }
    }
    return closest;
}

export function isConflictingColor(input) {
    return findConflictingReserved(input) !== null;
}

export function parseHexInput(input) {
    if (typeof input !== 'string') return null;
    const m = input.trim().match(/^#?([0-9a-f]{6})$/i);
    return m ? `#${m[1].toLowerCase()}` : null;
}

export function findPresetIndex(input) {
    const hex = normalizeColor(input);
    if (!hex) return -1;
    return CURATED_PALETTE.indexOf(hex);
}

// Pure decision logic for the RouteColorPicker hex input. Given a user
// draft string, returns a tagged result describing what the picker should do:
//
//   { action: 'noop' }                         — empty draft; picker clears warnings
//   { action: 'error', message }               — unparseable hex; picker shows error, no commit
//   { action: 'commit', value, warning?: string }
//       — parseable hex; picker calls onSelect(value). If `warning` is set the
//         hex is close to a reserved status color and the picker shows it.
//
// Extracted so the decision logic is unit-testable in node without jsdom.
export function evaluateHexSubmit(draft) {
    if (!draft) return { action: 'noop' };
    const parsed = parseHexInput(draft);
    if (!parsed) {
        return { action: 'error', message: 'Invalid hex (expected #RRGGBB)' };
    }
    const conflict = findConflictingReserved(parsed);
    if (conflict) {
        return {
            action: 'commit',
            value: parsed,
            warning: `Close to the reserved color ${conflict} — may be confusing on the map.`,
        };
    }
    return { action: 'commit', value: parsed };
}
